import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

export interface Crumb {
  label: string;
  path?: string;       // undefined = current page (not a link)
}

/**
 * Maps a path segment to a human-friendly Spanish label.
 * Segments not present fall back to a Title-Cased version of the slug.
 */
const SEGMENT_LABELS: Record<string, string> = {
  // top-level modules
  dashboard: 'Dashboard',
  production: 'Producción',
  quality: 'Calidad',
  logistics: 'Logística',
  materials: 'Materiales',
  maintenance: 'Mantenimiento',
  analytics: 'Analítica',
  traceability: 'Trazabilidad',
  backoffice: 'Back Office',
  mlops: 'MLOps',
  fai: 'FAI Lab',
  system: 'Sistema',

  // production
  dispatch: 'Despacho',
  assembly: 'Verificación Ensamblaje',
  cutting: 'Guía de Corte',
  cobotics: 'Cobótica',
  wip: 'WIP Board',
  'work-orders': 'Órdenes de Trabajo',
  timeline: 'Cronología',
  bom: 'BOM',
  routing: 'Ruta',
  costs: 'Costes',
  operators: 'Operadores',
  progress: 'Progreso',
  docs: 'Documentos',
  certs: 'Certificados',
  variance: 'Desviación',
  bottleneck: 'Cuello de Botella',
  'delay-risk': 'Riesgo de Retraso',

  // quality
  board: 'Tablero',
  'defect-detection': 'Detección de Defectos',
  'vision-3d': 'Visión 3D',
  packaging: 'Embalaje',
  calibration: 'Calibración',

  // logistics / materials
  warehouse: 'Almacén',
  inventory: 'Inventario',
  barcode: 'Códigos de Barras',
  search: 'Búsqueda',

  // maintenance
  predictive: 'Predictivo',
  condition: 'Condition Monitoring',
  safety: 'Seguridad / Compliance',

  // backoffice / devices
  devices: 'Dispositivos',
  cameras: 'Cámaras',
  camera: 'Cámara',
  edge: 'Edge',
  plc: 'PLC',
  firmware: 'Firmware',
  metrics: 'Métricas',
  'network-topology': 'Topología de Red',
  'floor-map': 'Mapa de Planta',
  sla: 'SLA',
  live: 'En Vivo',
  kpis: 'KPIs',
  detections: 'Detecciones',
  config: 'Configuración',
  health: 'Salud',
  compare: 'Comparar',

  // platform admin
  rules: 'Motor de Reglas',
  billing: 'Facturación',
  'erp-sync': 'Sync ERP',
  admin: 'Admin',
  'audit-log': 'Audit Log',
  reports: 'Reportes',

  // observability
  copilot: 'Copilot',
  alerts: 'Alertas',
  storage: 'Almacenamiento',
  cache: 'Caché',
  apm: 'APM',
  incidents: 'Incidencias',
  errors: 'Errores',

  // mlops
  sam2: 'SAM2 Studio',
  'model-factory': 'Model Factory',
  drift: 'Drift Monitoring',
  'zero-shot': 'Zero-Shot Explorer',
  'dataset-qa': 'Dataset QA',
  'vector-search': 'Vector Search',
  experiments: 'Experimentos',
  'onnx-pipeline': 'Pipeline ONNX',
  'vision-industrial': 'Visión Industrial',

  // fai
  dossier: 'Dossier',
};

function humanize(seg: string): string {
  return seg
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build a breadcrumb trail from the current URL pathname.
 *
 * - Skips the leading slash and ignores empty segments.
 * - Maps each segment via {@link SEGMENT_LABELS} (or humanizes the slug).
 * - Detects dynamic ID-like segments (UUID, WO-…, numeric) and shows them
 *   verbatim instead of being treated as a "section".
 * - The last item omits `path` so the UI can render it as the active page.
 */
export function useBreadcrumbs(): Crumb[] {
  const { pathname } = useLocation();
  const params = useParams();

  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return [{ label: 'Dashboard' }];

    const idValues = new Set(Object.values(params).filter(Boolean) as string[]);
    const isIdLike = (s: string) =>
      idValues.has(s) ||
      /^\d+$/.test(s) ||
      /^WO[-_]/i.test(s) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(s);

    const crumbs: Crumb[] = [{ label: 'Inicio', path: '/dashboard' }];
    let acc = '';
    segments.forEach((seg, i) => {
      acc += `/${seg}`;
      const label = isIdLike(seg) ? seg : SEGMENT_LABELS[seg] ?? humanize(seg);
      const isLast = i === segments.length - 1;
      crumbs.push({ label, path: isLast ? undefined : acc });
    });

    return crumbs;
  }, [pathname, params]);
}
